import React from 'react';
import { motion } from 'framer-motion';
import { Dumbbell, School as Pool, Users, Heart, ChevronRight, Sparkles, Waves, Wind, Snowflake, Activity, Clock, Target } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const features = [
  {
    icon: Dumbbell,
    title: 'Full Gym Access',
    description: 'Complete access to our premium gym floor and equipment',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    icon: Pool,
    title: 'Recovery Zone',
    description: 'Swimming pool, sauna, steam room, ice bath and spa pool',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    icon: Users,
    title: 'All Classes',
    description: 'Unlimited access to our group fitness classes',
    color: 'from-teal-500/20 to-emerald-500/20'
  }
];

const services = [
  {
    name: 'Swimming Pool',
    description: 'Two-lane 25m temperature-controlled pool perfect for recovery swims and aqua therapy',
    features: ['Temperature controlled', 'Professional lifeguard', 'Lane swimming', 'Aqua therapy'],
    image: 'https://i.ibb.co/LDrWz0Sg/DSC01349.jpg',
    icon: Pool,
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    name: 'Ice Bath',
    description: 'Cold immersion therapy at 4°C for inflammation reduction and recovery',
    features: ['4°C temperature', 'Reduces inflammation', 'Speeds recovery', 'Mental resilience'],
    image: 'https://i.ibb.co/Y733h978/DSC01360.jpg',
    icon: Snowflake,
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    name: 'Traditional Sauna',
    description: 'Traditional dry heat therapy at 80-90°C for muscle recovery and detoxification',
    features: ['80-90°C temperature', 'Muscle relaxation', 'Detoxification', 'Stress relief'],
    image: 'https://i.ibb.co/QvNpyQZ9/DSC01358.jpg',
    icon: Wind,
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    name: 'Group Classes',
    description: 'Wide range of classes including HIIT, strength, yoga, pilates, and more',
    features: ['Expert instructors', 'Various class types', 'All fitness levels', 'Scheduled sessions'],
    image: 'https://i.ibb.co/m58VTdb2/DSC01426-1.jpg',
    icon: Users,
    color: 'from-emerald-500/20 to-blue-500/20'
  },
  {
    name: 'Gym Floor',
    description: 'State-of-the-art equipment including cardio machines, free weights, and functional training areas',
    features: ['Premium equipment', 'Free weights', 'Cardio machines', 'Functional zones'],
    image: 'https://i.ibb.co/7NbNttFw/DSC01317.jpg',
    icon: Dumbbell,
    color: 'from-blue-500/20 to-purple-500/20'
  },
  {
    name: 'Steam Room',
    description: 'Humid heat therapy for respiratory health and muscle relaxation',
    features: ['Respiratory health', 'Muscle relaxation', 'Skin cleansing', 'Stress reduction'],
    image: 'https://i.ibb.co/kCQpt8n/Screenshot-2025-03-11-at-08-57-15.png',
    icon: Activity,
    color: 'from-purple-500/20 to-pink-500/20'
  }
];

export default function FitnessPassportPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://i.ibb.co/rGbPGjfq/DSC01317.jpg')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
            
            {/* Animated Background Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.15, scale: 1.2 }}
              transition={{ duration: 3, delay: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full blur-3xl"
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                className="inline-block mb-8"
              >
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-1 rounded-full shadow-lg shadow-blue-500/20">
                  <div className="bg-black px-8 py-3 rounded-full flex items-center space-x-3">
                    <Heart className="w-6 h-6 text-blue-400" />
                    <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                      FITNESS PASSPORT MEMBERS WELCOME
                    </span>
                    <Heart className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </motion.div>

              <div className="relative">
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-6xl sm:text-8xl font-bold mb-6"
                >
                  FULL ACCESS TO
                  <div className="relative inline-block mx-4">
                    <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400">
                      ALL SERVICES
                    </span>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.5, 0.8, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-cyan-500/30 to-teal-500/30 blur-2xl"
                    />
                  </div>
                </motion.h1>
                <p className="text-2xl text-gray-400">Experience everything Fitness Portal has to offer</p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="mt-12"
              >
                <a
                  href="#services"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                >
                  <span>EXPLORE OUR FACILITIES</span>
                  <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl sm:text-7xl font-bold mb-4">EVERYTHING INCLUDED</h2>
              <p className="text-xl text-gray-400">Your Fitness Passport gives you complete access</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className="relative group"
                >
                  <motion.div
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl blur-xl`}
                  />
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 h-full transform hover:scale-105 transition-transform duration-500">
                    <div className={`bg-gradient-to-br ${feature.color} p-4 rounded-xl w-fit mb-6`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-400 text-lg">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl sm:text-7xl font-bold mb-4">OUR SERVICES</h2>
              <p className="text-xl text-gray-400">Explore our world-class facilities</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group h-full"
                >
                  <motion.div
                    animate={{
                      opacity: [0.3, 0.6, 0.3],
                      scale: [1, 1.02, 1]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl blur-xl`}
                  />
                  <div className="relative h-full bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center space-x-3">
                          <div className={`bg-gradient-to-br ${service.color} p-3 rounded-full`}>
                            <service.icon className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-2xl font-bold text-white">{service.name}</h3>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-300 mb-6 text-lg">{service.description}</p>
                      <div className="grid grid-cols-2 gap-3">
                        {service.features.map((feature, i) => (
                          <div
                            key={i}
                            className={`bg-gradient-to-r ${service.color} rounded-xl p-3 backdrop-blur-sm transition-transform duration-300 hover:scale-105`}
                          >
                            <span className="text-white">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Welcome Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden rounded-2xl"
            >
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1, 1.05, 1]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"
              />
              <motion.div
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                  scale: [1.05, 1, 1.05]
                }}
                transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
                className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-full blur-3xl"
              />
              
              <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="flex items-center space-x-3 mb-6">
                  <Sparkles className="w-8 h-8 text-blue-400" />
                  <h2 className="text-4xl font-bold">Welcome to Fitness Portal</h2>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed mb-8">
                  We're proud to partner with Fitness Passport to provide you with complete access to our premium facilities. Your Fitness Passport membership includes everything we offer - from our state-of-the-art gym floor to our luxurious recovery facilities.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-xl p-6 transform hover:scale-105 transition-transform duration-300">
                    <h4 className="text-xl font-bold mb-2">How to Access</h4>
                    <p className="text-gray-400">Simply present your Fitness Passport at reception during your first visit. Our team will help you get started!</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-6 transform hover:scale-105 transition-transform duration-300">
                    <h4 className="text-xl font-bold mb-2">Questions?</h4>
                    <p className="text-gray-400">Our reception team is here to help with any questions about your Fitness Passport access.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}